import { object, string, number, mixed, array, boolean } from "yup";

import { fetchData } from "@utils/api";
import { Roles } from "@utils/settings";
import { APIMethods, ApiResponse } from "@typeDefinitions";

import { LangDto } from "@client";
import {
  ClinicSubscriptionResource,
  clinicSubscriptionSchema,
} from "@client/resources/ClinicSubscriptionResource";

export const fetchDietitianAccount = async (): Promise<
  ApiResponse<FetchDietitianAccountResponse>
> => {
  const response = await fetchData("/dietitian/account", APIMethods.GET);
  return fetchDietitianAccountSchema.validate(response);
};

export interface FetchDietitianAccountResponse {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  roles: Roles[];
  phone: PhoneDto;
  language: LangDto;
  clinic: ClinicDto | null;
  billing: BillingDto | null;
  avatar: string | null;
  chat: ChatDto | null;
  permissions: PermissionsDto;
  twoFactorEnabled: boolean;
  bite: BiteDto | null;
}

interface BiteDto {
  onboardingCompleted: boolean;
}

interface PermissionsDto {
  canViewAllPatients: boolean;
}

interface ChatDto {
  description: string | null;
}

interface PhoneDto {
  prefix: string | null;
  number: string | null;
}

interface BillingDto {
  day: number;
  currentPeriod: {
    startedAt: string;
    endedAt: string;
  };
}

export enum ClinicType {
  BASIC = 1,
  SCHOOL = 2,
}
interface ClinicDto {
  id: number;
  subscription: SubscriptionDto;
  subscriptionNew: ClinicSubscriptionResource | null;
  type: ClinicType;
  whitelabel: WhitelabelDto;
  appId: string | null;
  hasApiAccess: boolean;
}

interface WhitelabelDto {
  motherClinic: null | { name: string };
  partnerClinic: boolean;
}
export interface SubscriptionDto {
  finishedAt: string | null;
  type: TypeDto;
}

export interface SubscriptionNameDto {
  id: number;
  name: string;
}

interface TypeDto {
  id: number;
}

const phoneSchema = object().shape({
  prefix: string().nullable().default(null),
  number: string().nullable().default(null),
});

const typeSchema = object().shape({
  id: number().required(),
});

const subscriptionSchema = object().shape({
  finishedAt: string().nullable().default(null),
  type: typeSchema.required(),
});

const subscriptionNameSchema = object().shape({
  id: number().required(),
  name: string().required(),
});

const billingSchema = object().shape({
  day: number().min(1).required(),
  currentPeriod: object()
    .shape({
      startedAt: string().required(),
      endedAt: string().required(),
    })
    .required(),
});

const motherClinicSchema = object({
  name: string().required(),
});

const whitelabelSchema = object().shape({
  motherClinic: motherClinicSchema.nullable().default(null),
  partnerClinic: boolean().required(),
});
const clinicSchema = object().shape({
  id: number().required(),
  subscription: subscriptionSchema.required(),
  subscriptionNew: clinicSubscriptionSchema.nullable().default(null),
  type: mixed<ClinicType>()
    .oneOf(Object.values(ClinicType) as ClinicType[])
    .required(),
  whitelabel: whitelabelSchema.required(),
  appId: string().nullable().default(null),
  hasApiAccess: boolean().required(),
});

const permissionsSchema = object().shape({
  canViewAllPatients: boolean().required(),
});

const chatSchema = object().shape({
  description: string().nullable().default(null),
});

const biteSchema = object().shape({
  onboardingCompleted: boolean().default(false),
});

const dietitianAccountSchema = object().shape({
  id: number().required(),
  firstName: string().required(),
  lastName: string().required(),
  email: string().required(),
  avatar: string().nullable().default(null),
  roles: array()
    .of(mixed<Roles>().oneOf(Object.values(Roles)).required())
    .required(),
  phone: phoneSchema.required(),
  language: mixed<LangDto>().oneOf(Object.values(LangDto)).required(),
  clinic: clinicSchema.nullable().default(null),
  billing: billingSchema.nullable().default(null),
  chat: chatSchema.nullable().default(null),
  permissions: permissionsSchema.required(),
  twoFactorEnabled: boolean().required(),
  bite: biteSchema.nullable().default(null),
});

const fetchDietitianAccountSchema = object().shape({
  data: dietitianAccountSchema.required(),
});
